import { DialogSection, DialogNode, DialogDataContent, DialogField, DialogOption, ConditionOption } from '@/lib/stores/dialogStore';

/**
 * Simple UUID generator for creating unique IDs
 */
export const generateId = (): string => {
  return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
};

/**
 * Strip HTML tags from a string for validation purposes
 */
export const stripHtml = (html: string): string => {
  const tmp = document.createElement('div');
  tmp.innerHTML = html;
  return tmp.textContent || tmp.innerText || '';
};

/**
 * Generate a section identifier from a timestamp
 */
export const generateSectionIdentifier = (index?: number): string => {
  const suffix = index !== undefined ? `_${index}` : `_${Math.random().toString(36).substr(2, 5)}`;
  return `section_${Date.now()}${suffix}`;
};

/**
 * Ensure all sections have stable IDs for drag-and-drop
 */
export const ensureSectionIds = (sections: DialogSection[]): DialogSection[] => {
  return sections.map((section, index) => ({
    ...section,
    id: section.id || `section_${Date.now()}_${index}`,
  }));
};

/**
 * Check if a string is a UUID format (database ID)
 */
const isUUID = (str: string): boolean => {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
};

/**
 * Sanitize section identifiers - replace UUIDs and malformed identifiers with proper ones
 */
const sanitizeSectionIdentifiers = (sections: DialogSection[]): { 
  sections: DialogSection[]; 
  renameMap: Map<string, string>;
} => {
  const renameMap = new Map<string, string>();
  
  const sanitizedSections = sections.map((section) => {
    const identifier = section.section_identifier;
    
    // If identifier is a UUID or otherwise malformed, replace it
    if (isUUID(identifier) || !identifier.startsWith('section_')) {
      const newIdentifier = generateSectionIdentifier();
      renameMap.set(identifier, newIdentifier);
      return {
        ...section,
        section_identifier: newIdentifier,
      };
    }
    
    return section;
  });
  
  return { sections: sanitizedSections, renameMap };
};

/**
 * Deduplicate section identifiers in sections array.
 * If duplicates are found, rename them with unique identifiers and update all references.
 */
export const deduplicateSectionIdentifiers = (sections: DialogSection[]): DialogSection[] => {
  // First, sanitize any malformed identifiers (UUIDs, etc.)
  const { sections: sanitizedSections, renameMap: sanitizeRenameMap } = sanitizeSectionIdentifiers(sections);
  
  // Now work with sanitized sections for deduplication
  const workingSections = sanitizedSections;
  // Track seen identifiers and their first occurrence index
  const seenIdentifiers = new Map<string, number>(); // identifier -> first occurrence index
  const renameMap = new Map<string, string>(); // section index -> new identifier
  
  // First pass: detect duplicates and assign new identifiers
  workingSections.forEach((section, index) => {
    const identifier = section.section_identifier;
    
    if (seenIdentifiers.has(identifier)) {
      // This is a duplicate - generate new unique identifier for THIS section
      const newIdentifier = generateSectionIdentifier();
      renameMap.set(`section_${index}`, newIdentifier);
    } else {
      // First occurrence - keep it unchanged
      seenIdentifiers.set(identifier, index);
    }
  });
  
  // Second pass: rename sections that are duplicates
  const renamedSections = workingSections.map((section, index) => {
    const newIdentifier = renameMap.get(`section_${index}`);
    if (newIdentifier) {
      return {
        ...section,
        section_identifier: newIdentifier,
      };
    }
    return section;
  });
  
  // Build combined old -> new mapping for reference updates
  // Includes both sanitized identifiers and deduplicated identifiers
  const oldToNewMap = new Map<string, string>(sanitizeRenameMap);
  
  // Add deduplication renames
  sections.forEach((section, index) => {
    const newIdentifier = renameMap.get(`section_${index}`);
    if (newIdentifier) {
      // Map from the original (pre-sanitization) identifier
      oldToNewMap.set(section.section_identifier, newIdentifier);
    }
  });
  
  // Third pass: update all navigation references
  return renamedSections.map(section => ({
    ...section,
    fields: section.fields?.map(field => updateFieldNavigationReferences(field, oldToNewMap)) || [],
  }));
};

/**
 * Update field navigation references based on rename map
 */
const updateFieldNavigationReferences = (
  field: DialogField,
  renameMap: Map<string, string>
): DialogField => {
  const updatedField = { ...field };
  
  // Update direct navigation
  if (updatedField.next_section && renameMap.has(updatedField.next_section)) {
    updatedField.next_section = renameMap.get(updatedField.next_section)!;
  }
  
  // Update legacy condition branches
  if (updatedField.next_section_true && renameMap.has(updatedField.next_section_true)) {
    updatedField.next_section_true = renameMap.get(updatedField.next_section_true)!;
  }
  if (updatedField.next_section_false && renameMap.has(updatedField.next_section_false)) {
    updatedField.next_section_false = renameMap.get(updatedField.next_section_false)!;
  }
  
  // Update options navigation
  if (updatedField.options) {
    updatedField.options = updatedField.options.map(option => {
      if (option.next_section && renameMap.has(option.next_section)) {
        return { ...option, next_section: renameMap.get(option.next_section)! };
      }
      return option;
    });
  }
  
  // Update condition options navigation
  if (updatedField.conditionOptions) {
    updatedField.conditionOptions = updatedField.conditionOptions.map(condOption => {
      if (condOption.next_section && renameMap.has(condOption.next_section)) {
        return { ...condOption, next_section: renameMap.get(condOption.next_section)! };
      }
      return condOption;
    });
  }
  
  return updatedField;
};

/**
 * Migrate old 'text' type to 'dialog' type
 */
export const migrateTextToDialog = (sections: DialogSection[]): DialogSection[] => {
  return sections.map(section => ({
    ...section,
    fields: section.fields?.map(field => ({
      ...field,
      type: (field.type as any) === 'text' ? 'dialog' : field.type
    })) || []
  }));
};

/**
 * Migrate 'end-text' type to 'end-dialog' type
 * The end-text type has been deprecated in favor of end-dialog
 */
export const migrateEndTextToEndDialog = (sections: DialogSection[]): DialogSection[] => {
  return sections.map(section => ({
    ...section,
    fields: section.fields?.map(field => ({
      ...field,
      type: field.type === 'end-text' ? 'end-dialog' : field.type
    })) || []
  }));
};

/**
 * Migrate end-text nodes to end-dialog in the nodes array
 */
export const migrateEndTextNodes = (nodes: DialogNode[]): DialogNode[] => {
  return nodes.map(node => ({
    ...node,
    type: node.type === 'end-text' ? 'end-dialog' : node.type
  }));
};

/**
 * Convert nodes array to sections array for edit mode UI.
 * 
 * This converts the source-of-truth nodes structure to the sections view model
 * that the edit UI expects. Each node becomes a section with a single field.
 * 
 * @param nodes - Array of dialog nodes (source of truth)
 * @returns Array of dialog sections (view model for UI)
 */
export const convertNodesToSections = (nodes: DialogNode[]): DialogSection[] => {
  return nodes.map((node, index) => ({
    id: node.id,
    dialog_id: node.dialog_id,
    name: node.name || `Node ${index + 1}`,
    section_identifier: node.node_identifier,
    order_index: index,
    fields: [{
      id: node.id,
      section_id: node.dialog_id,
      type: node.type,
      text: node.text,
      translatable: node.translatable,
      string_id: node.string_id,
      speaker: node.speaker,
      order_index: 0,
      
      // Navigation - map node references to section references for UI
      next_section: node.next_node,
      next_field: node.next_field,
      next_dialog: node.next_dialog,
      next_dialog_section: node.next_dialog_node,
      
      // Type-specific properties (only include those relevant to the current type)
      condition: node.type === 'condition' ? node.condition : undefined,
      conditionOptions: node.type === 'condition' ? node.conditionOptions : undefined,
      options: node.type === 'question' ? node.options : undefined,
      setter: node.type === 'setter' ? node.setter : undefined,
      
      // Legacy condition properties
      next_section_true: node.next_section_true,
      next_section_false: node.next_section_false,
      next_dialog_true: node.next_dialog_true,
      next_dialog_false: node.next_dialog_false,
      next_dialog_section_true: node.next_dialog_section_true,
      next_dialog_section_false: node.next_dialog_section_false,
    }],
    fieldOrder: [node.id],
  }));
};

/**
 * Clean up references to a removed section identifier
 */
export const cleanupSectionReferences = (
  sections: DialogSection[],
  removedIdentifier: string
): DialogSection[] => {
  return sections.map(section => ({
    ...section,
    fields: section.fields?.map(field => {
      const updatedField = { ...field };
      
      // Clean up field-level next_section references
      if (updatedField.next_section === removedIdentifier) {
        updatedField.next_section = null;
      }
      
      // Clean up condition field branches
      if (updatedField.type === 'condition') {
        if (updatedField.next_section_true === removedIdentifier) {
          updatedField.next_section_true = null;
        }
        if (updatedField.next_section_false === removedIdentifier) {
          updatedField.next_section_false = null;
        }
      }
      
      // Clean up option references
      if (updatedField.type === 'question' && updatedField.options) {
        updatedField.options = updatedField.options.map(option => {
          if (option.next_section === removedIdentifier) {
            return { ...option, next_section: null };
          }
          return option;
        });
      }
      
      return updatedField;
    }) || []
  }));
};

/**
 * Get all valid section identifiers from sections
 */
export const getValidSectionIdentifiers = (sections: DialogSection[]): Set<string> => {
  return new Set(sections.map(s => s.section_identifier));
};

/**
 * Deep clone an object using JSON serialization
 */
export const deepClone = <T>(obj: T): T => {
  return JSON.parse(JSON.stringify(obj));
};

// ============================================================================
// MIGRATION UTILITIES (SECTION → NODE)
// ============================================================================

/**
 * Migrate legacy section structure to new node structure.
 * Converts sections with multiple fields into separate nodes.
 */
export const migrateDialogSectionsToNodes = (data: DialogDataContent): DialogDataContent => {
  // If already migrated (has nodes), return as-is
  if (data.nodes && data.nodes.length > 0) {
    return data;
  }
  
  // If no sections, return empty nodes array
  if (!data.sections || data.sections.length === 0) {
    return {
      ...data,
      nodes: [],
      startingNode: undefined,
    };
  }
  
  const nodes: DialogNode[] = [];
  let globalOrderIndex = 0;
  
  // Build a map of section_identifier → node_identifier for the first field in each section
  // This is used to update navigation references
  const sectionToNodeMap = new Map<string, string>();
  
  // First pass: Create nodes and build the mapping
  data.sections.forEach((section) => {
    const fields = section.fields || [];
    
    if (fields.length === 0) {
      // Empty section - create a placeholder dialog node
      const nodeIdentifier = section.section_identifier;
      sectionToNodeMap.set(section.section_identifier, nodeIdentifier);
      
      nodes.push({
        id: section.id || generateId(),
        dialog_id: section.dialog_id,
        name: section.name || `Section ${section.order_index + 1}`,
        node_identifier: nodeIdentifier,
        order_index: globalOrderIndex++,
        type: 'dialog',
        text: '',
        translatable: false,
        string_id: null,
      });
    } else {
      // Process each field as a separate node
      fields.forEach((field, fieldIndex) => {
        const isFirstField = fieldIndex === 0;
        
        // Generate node identifier
        // First field gets the section_identifier, others get suffixed versions
        let nodeIdentifier: string;
        if (isFirstField) {
          nodeIdentifier = section.section_identifier;
          sectionToNodeMap.set(section.section_identifier, nodeIdentifier);
        } else {
          nodeIdentifier = `${section.section_identifier}_${fieldIndex + 1}`;
        }
        
        // Generate node name
        // First field gets the section name, others get suffixed names
        let nodeName: string;
        if (isFirstField) {
          nodeName = section.name || `Section ${section.order_index + 1}`;
        } else {
          nodeName = `${section.name || `Section ${section.order_index + 1}`}_${fieldIndex + 1}`;
        }
        
        // Create the node (without updating navigation yet)
        const node: DialogNode = {
          id: field.id,
          dialog_id: section.dialog_id,
          name: nodeName,
          node_identifier: nodeIdentifier,
          order_index: globalOrderIndex++,
          type: field.type,
          text: field.text || '',
          translatable: field.translatable,
          string_id: field.string_id,
          speaker: field.speaker,
          
          // Copy navigation as-is for now (will update in second pass)
          next_section: field.next_section,
          next_field: field.next_field,
          next_dialog: field.next_dialog,
          next_dialog_section: field.next_dialog_section,
          
          // Type-specific properties (only include those relevant to the current type)
          condition: field.type === 'condition' ? field.condition : undefined,
          conditionOptions: field.type === 'condition' ? field.conditionOptions : undefined,
          options: field.type === 'question' ? field.options : undefined,
          setter: field.type === 'setter' ? field.setter : undefined,
          
          // Legacy condition properties
          next_section_true: field.next_section_true,
          next_section_false: field.next_section_false,
          next_dialog_true: field.next_dialog_true,
          next_dialog_false: field.next_dialog_false,
          next_dialog_section_true: field.next_dialog_section_true,
          next_dialog_section_false: field.next_dialog_section_false,
        };
        
        nodes.push(node);
      });
    }
  });
  
  // Second pass: Update all navigation references
  const migratedNodes = nodes.map(node => updateNodeNavigationReferences(node, sectionToNodeMap));
  
  // Update starting reference
  const startingNode = data.startingSection 
    ? sectionToNodeMap.get(data.startingSection) 
    : (migratedNodes.length > 0 ? migratedNodes[0].node_identifier : undefined);
  
  // Create new sections array from nodes (one field per section for edit mode)
  const migratedSections: DialogSection[] = migratedNodes.map((node, index) => ({
    id: node.id,
    dialog_id: node.dialog_id,
    name: node.name,
    section_identifier: node.node_identifier,
    order_index: index,
    fields: [{
      id: node.id,
      type: node.type,
      text: node.text,
      translatable: node.translatable,
      string_id: node.string_id,
      speaker: node.speaker,
      order_index: 0,
      
      // Navigation
      next_section: node.next_node,
      next_field: node.next_field,
      next_dialog: node.next_dialog,
      next_dialog_section: node.next_dialog_node,
      
      // Type-specific properties
      condition: node.condition,
      conditionOptions: node.conditionOptions,
      options: node.options,
      setter: node.setter,
      
      // Legacy condition properties
      next_section_true: node.next_section_true,
      next_section_false: node.next_section_false,
      next_dialog_true: node.next_dialog_true,
      next_dialog_false: node.next_dialog_false,
      next_dialog_section_true: node.next_dialog_section_true,
      next_dialog_section_false: node.next_dialog_section_false,
    }],
    fieldOrder: [node.id],
  }));
  
  return {
    ...data,
    nodes: migratedNodes,
    startingNode,
    // Update sections to match new structure (one field per section)
    sections: migratedSections,
    startingSection: startingNode,
  };
};

/**
 * Update navigation references in a node from section_identifier to node_identifier.
 * Handles direct references and references within options/conditions.
 */
const updateNodeNavigationReferences = (
  node: DialogNode, 
  sectionToNodeMap: Map<string, string>
): DialogNode => {
  const updatedNode = { ...node };
  
  // Update direct navigation
  if (updatedNode.next_section) {
    updatedNode.next_node = sectionToNodeMap.get(updatedNode.next_section) || updatedNode.next_section;
  }
  
  // Update options navigation
  if (updatedNode.options) {
    updatedNode.options = updatedNode.options.map(option => ({
      ...option,
      node_id: option.field_id,
      next_node: option.next_section 
        ? sectionToNodeMap.get(option.next_section) || option.next_section 
        : option.next_node,
      next_dialog_node: option.next_dialog_section || option.next_dialog_node,
    }));
  }
  
  // Update condition options navigation
  if (updatedNode.conditionOptions) {
    updatedNode.conditionOptions = updatedNode.conditionOptions.map(condOption => ({
      ...condOption,
      next_node: condOption.next_section 
        ? sectionToNodeMap.get(condOption.next_section) || condOption.next_section 
        : condOption.next_node,
      next_dialog_node: condOption.next_dialog_section || condOption.next_dialog_node,
    }));
  }
  
  // Update legacy condition branches
  if (updatedNode.next_section_true) {
    updatedNode.next_node = sectionToNodeMap.get(updatedNode.next_section_true) || updatedNode.next_section_true;
  }
  if (updatedNode.next_section_false) {
    // For false branch, we'd need more complex logic, but for now just map it
    updatedNode.next_node = sectionToNodeMap.get(updatedNode.next_section_false) || updatedNode.next_section_false;
  }
  
  // Update dialog section references
  if (updatedNode.next_dialog_section) {
    updatedNode.next_dialog_node = updatedNode.next_dialog_section;
  }
  
  return updatedNode;
};

/**
 * Ensure all nodes have stable IDs for drag-and-drop.
 */
export const ensureNodeIds = (nodes: DialogNode[]): DialogNode[] => {
  return nodes.map((node, index) => ({
    ...node,
    id: node.id || `node_${Date.now()}_${index}`,
  }));
};

/**
 * Clean up references to a removed node identifier.
 */
export const cleanupNodeReferences = (
  nodes: DialogNode[],
  removedIdentifier: string
): DialogNode[] => {
  return nodes.map(node => {
    const updatedNode = { ...node };
    
    // Clean up node-level next_node references
    if (updatedNode.next_node === removedIdentifier) {
      updatedNode.next_node = null;
    }
    
    // Clean up legacy section references
    if (updatedNode.next_section === removedIdentifier) {
      updatedNode.next_section = null;
    }
    
    // Clean up condition node branches
    if (updatedNode.type === 'condition') {
      if (updatedNode.next_section_true === removedIdentifier) {
        updatedNode.next_section_true = null;
      }
      if (updatedNode.next_section_false === removedIdentifier) {
        updatedNode.next_section_false = null;
      }
      
      // Clean up conditionOptions
      if (updatedNode.conditionOptions) {
        updatedNode.conditionOptions = updatedNode.conditionOptions.map(condOption => {
          if (condOption.next_node === removedIdentifier || condOption.next_section === removedIdentifier) {
            return { ...condOption, next_node: null, next_section: null };
          }
          return condOption;
        });
      }
    }
    
    // Clean up option references
    if (updatedNode.type === 'question' && updatedNode.options) {
      updatedNode.options = updatedNode.options.map(option => {
        if (option.next_node === removedIdentifier || option.next_section === removedIdentifier) {
          return { ...option, next_node: null, next_section: null };
        }
        return option;
      });
    }
    
    return updatedNode;
  });
};

/**
 * Get all valid node identifiers from nodes.
 */
export const getValidNodeIdentifiers = (nodes: DialogNode[]): Set<string> => {
  return new Set(nodes.map(n => n.node_identifier));
};

